import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ClipboardCheck, FileSignature, FileText, CheckCircle2, AlertTriangle,
  Loader2, RotateCcw, Ban, ChevronDown, ChevronRight, Wand2, Database, DollarSign, X,
} from "lucide-react";

const KIND_LABEL = {
  reps_certs: "Reps & Certs",
  amendment_ack: "Amendment acknowledgment",
  signable_form: "Signable form",
  fillable_form: "Fillable form",
};

const DONE = ["filled", "served", "covered", "na", "not_applicable", "attested"];

function StatusPill({ status }) {
  const done = DONE.includes(status);
  const na = status === "na" || status === "not_applicable";
  const tone = na
    ? "bg-zinc-100 text-zinc-600 border-zinc-300"
    : done
    ? "bg-emerald-100 text-emerald-700 border-emerald-300"
    : "bg-amber-100 text-amber-800 border-amber-300";
  const Icon = na ? Ban : done ? CheckCircle2 : AlertTriangle;
  const label = na ? "Not applicable" : done ? "Addressed" : "Pending";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${tone}`} data-testid="intake-form-status">
      <Icon className="h-3 w-3" /> {label}
    </span>
  );
}

export function IntakeFormsPanel({ dealId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const [rescanning, setRescanning] = useState(false);
  const [editFor, setEditFor] = useState(null); // form_id being flagged N/A
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [afBusy, setAfBusy] = useState(null); // form_id currently autofilling
  const [pickerFor, setPickerFor] = useState(null); // form_id for the past-performance picker

  const load = async () => {
    try {
      const r = await api.get(`/bids/${dealId}/intake-forms`);
      setData(r.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load intake forms when the deal id changes
  useEffect(() => { load(); }, [dealId]);

  const rescan = async () => {
    setRescanning(true);
    try {
      await api.post(`/bids/${dealId}/intake-forms/rescan`, {});
      await load();
      toast.success("Intake scan re-run");
    } catch (e) { toast.error("Re-scan failed"); }
    finally { setRescanning(false); }
  };

  const dispose = async (formId, status, why) => {
    setBusy(true);
    try {
      await api.post(`/bids/${dealId}/intake-forms/${formId}/disposition`, { status, reason: why || "" });
      setEditFor(null); setReason("");
      await load();
      toast.success(status === "na" ? "Flagged not-applicable" : "Marked filled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally { setBusy(false); }
  };

  const autofill = async (formId, mode, referenceIds) => {
    setAfBusy(formId);
    try {
      const r = await api.post(`/bids/${dealId}/intake-forms/${formId}/autofill`,
        { mode, reference_ids: referenceIds || undefined });
      setPickerFor(null);
      await load();
      toast.success(r?.data?.disposition?.reason || "Form autofilled & merged into the package");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Autofill failed");
    } finally { setAfBusy(null); }
  };

  const AF_LABEL = {
    pricing_sheet: { text: "Autofill priced schedule", Icon: DollarSign },
    sources_sought: { text: "Autofill from profile", Icon: Wand2 },
    past_performance: { text: "Fill from Data Library", Icon: Database },
  };

  if (loading) return null;
  if (!data || !data.forms || data.forms.length === 0) return null;

  const pending = data.pending_count || 0;
  const gateClear = (data.blockers || []).length === 0;

  return (
    <div className="mt-5 border border-zinc-200 rounded-xl bg-white shadow-sm" data-testid="intake-forms-panel">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="intake-forms-toggle">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-4 w-4 text-gold-dark" />
          <span className="text-sm font-semibold text-zinc-900">Intake forms checklist</span>
          <span className="text-[11px] text-zinc-500">
            {data.identified_count} identified · {data.scanned_pdf_count} doc(s) scanned
          </span>
          {pending > 0 ? (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border bg-amber-100 text-amber-800 border-amber-300" data-testid="intake-forms-pending-count">
              <AlertTriangle className="h-3 w-3" /> {pending} pending
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border bg-emerald-100 text-emerald-700 border-emerald-300" data-testid="intake-forms-clear">
              <CheckCircle2 className="h-3 w-3" /> All addressed
            </span>
          )}
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-zinc-400" /> : <ChevronRight className="h-4 w-4 text-zinc-400" />}
      </button>

      {open && (
        <div className="px-4 pb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] text-zinc-500 max-w-2xl">
              Every fillable or signable offeror form found in the solicitation must be filled/served or flagged
              not-applicable before the package can be generated (Fable-5 intake rule).
            </p>
            <button
              onClick={rescan}
              disabled={rescanning}
              className="flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-800 disabled:opacity-50"
              data-testid="intake-forms-rescan">
              {rescanning ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} Re-scan
            </button>
          </div>

          {!gateClear && (
            <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-800" data-testid="intake-forms-gate-warning">
              Package generation is held until every identified form is addressed.
            </div>
          )}

          <div className="space-y-2">
            {data.forms.map((f) => {
              const isSig = f.signature_required;
              const Icon = isSig ? FileSignature : FileText;
              const done = DONE.includes(f.effective_status);
              return (
                <div key={f.form_id} className="rounded-lg border border-zinc-200 px-3 py-2" data-testid={`intake-form-row-${f.form_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Icon className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
                        <span className="text-xs font-medium text-zinc-900 truncate" title={f.filename}>{f.filename}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-[10px] text-zinc-500">{KIND_LABEL[f.kind] || f.kind}</span>
                        {isSig && <span className="text-[10px] text-rose-600">· signature required</span>}
                        {f.effective_reason && <span className="text-[10px] text-zinc-400 italic truncate max-w-md">· {f.effective_reason}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <StatusPill status={f.effective_status} />
                    </div>
                  </div>

                  {!done && editFor !== f.form_id && (
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {f.autofill && (
                        <button
                          onClick={() => f.autofill === "past_performance" ? setPickerFor(f.form_id) : autofill(f.form_id, f.autofill)}
                          disabled={busy || afBusy === f.form_id}
                          className="inline-flex items-center gap-1 text-[11px] bg-gold-dark/10 text-gold-dark border border-gold-dark/40 px-2.5 py-1 rounded-md hover:bg-gold-dark/20 disabled:opacity-50 font-semibold"
                          data-testid={`intake-form-autofill-${f.form_id}`}>
                          {afBusy === f.form_id
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : React.createElement((AF_LABEL[f.autofill] || {}).Icon || Wand2, { className: "h-3 w-3" })}
                          {(AF_LABEL[f.autofill] || {}).text || "Autofill"}
                        </button>
                      )}
                      <button
                        onClick={() => dispose(f.form_id, "filled")}
                        disabled={busy}
                        className="text-[11px] bg-emerald-600 text-white px-2.5 py-1 rounded-md hover:bg-emerald-700 disabled:opacity-50"
                        data-testid={`intake-form-mark-filled-${f.form_id}`}>
                        Mark filled/served
                      </button>
                      <button
                        onClick={() => { setEditFor(f.form_id); setReason(""); }}
                        disabled={busy}
                        className="text-[11px] border border-zinc-300 text-zinc-700 px-2.5 py-1 rounded-md hover:bg-zinc-50 disabled:opacity-50"
                        data-testid={`intake-form-flag-na-${f.form_id}`}>
                        Flag not-applicable
                      </button>
                    </div>
                  )}

                  {editFor === f.form_id && (
                    <div className="flex items-center gap-2 mt-2" data-testid={`intake-form-na-editor-${f.form_id}`}>
                      <input
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Reason (required, logged to audit)"
                        className="flex-1 bg-white border border-zinc-300 rounded px-2 py-1 text-[11px] text-zinc-900 outline-none focus:border-gold-dark"
                        data-testid={`intake-form-na-reason-${f.form_id}`} />
                      <button
                        onClick={() => dispose(f.form_id, "na", reason)}
                        disabled={busy || !reason.trim()}
                        className="text-[11px] bg-zinc-900 text-white px-2.5 py-1 rounded-md hover:bg-zinc-800 disabled:opacity-50"
                        data-testid={`intake-form-na-save-${f.form_id}`}>
                        Save N/A
                      </button>
                      <button
                        onClick={() => { setEditFor(null); setReason(""); }}
                        className="text-[11px] text-zinc-400 hover:text-zinc-700">
                        Cancel
                      </button>
                    </div>
                  )}

                  {done && f.officer_set && (
                    <button
                      onClick={() => dispose(f.form_id, "pending", "")}
                      disabled={busy}
                      className="mt-2 flex items-center gap-1 text-[10px] text-zinc-400 hover:text-zinc-700"
                      data-testid={`intake-form-reopen-${f.form_id}`}>
                      <RotateCcw className="h-2.5 w-2.5" /> Re-open
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {pickerFor && (
        <PastPerfPicker
          dealId={dealId}
          busy={afBusy === pickerFor}
          onClose={() => setPickerFor(null)}
          onFill={(ids) => autofill(pickerFor, "past_performance", ids)}
        />
      )}
    </div>
  );
}

function PastPerfPicker({ dealId, busy, onClose, onFill }) {
  const [refs, setRefs] = useState(null);
  const [sel, setSel] = useState([]);
  useEffect(() => {
    api.get(`/bids/${dealId}/past-performance/candidates`)
      .then((r) => setRefs(r.data?.references || []))
      .catch(() => setRefs([]));
  }, [dealId]);
  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="w-full max-w-lg bg-white border border-zinc-200 rounded-xl p-5 max-h-[85vh] overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()} data-testid="past-perf-picker">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display font-bold text-lg text-zinc-900 flex items-center gap-2">
            <Database className="h-4 w-4 text-gold-dark" /> Pull from Data Library
          </h3>
          <button onClick={onClose} data-testid="past-perf-picker-close"><X className="h-4 w-4 text-zinc-400 hover:text-zinc-900" /></button>
        </div>
        <p className="text-[11px] text-zinc-500 mb-3">
          Select the completed contracts to fill onto the Past Performance form. Only fields stored on each
          reference are printed — a missing field stays a blank for you to complete (never fabricated).
        </p>
        {refs === null && <div className="py-6 text-center text-zinc-400 text-sm"><Loader2 className="h-4 w-4 animate-spin inline" /> Loading…</div>}
        {refs && refs.length === 0 && (
          <div className="py-6 text-center text-zinc-500 text-sm" data-testid="past-perf-empty">
            No references in the Data Library yet — add completed contracts in the Brain first.
          </div>
        )}
        <div className="space-y-2">
          {(refs || []).map((r) => (
            <label key={r.id} data-testid={`past-perf-ref-${r.id}`}
              className={`flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer ${sel.includes(r.id) ? "border-gold-dark bg-gold/5" : "border-zinc-200 hover:border-zinc-400"}`}>
              <input type="checkbox" checked={sel.includes(r.id)} onChange={() => toggle(r.id)}
                className="mt-1" data-testid={`past-perf-ref-check-${r.id}`} />
              <div className="min-w-0">
                <div className="text-xs font-semibold text-zinc-900">{r.project_name}</div>
                <div className="text-[10px] text-zinc-500">{[r.client, r.value, r.period].filter(Boolean).join(" · ") || "—"}</div>
                {!r.complete && (
                  <div className="text-[10px] text-amber-700 mt-0.5">
                    Incomplete — missing: {r.missing.join(", ")} (complete in the Brain for a full record)
                  </div>
                )}
              </div>
            </label>
          ))}
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 py-2 rounded-md border border-zinc-300 text-zinc-600 text-sm">Cancel</button>
          <button data-testid="past-perf-fill-btn" disabled={busy || sel.length === 0} onClick={() => onFill(sel)}
            className="flex-1 py-2 rounded-md bg-zinc-900 text-white font-semibold text-sm hover:bg-zinc-800 disabled:opacity-40">
            {busy ? "Filling…" : `Fill form (${sel.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}
